/* istanbul ignore file */

import { useCallback } from 'react';

import type { PartialProduct } from '@order/graphql';
import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

// ────────────────────────────────────────────────────────────────────────────────

export const useBagLineItemsTelemetry = (
  props: UseBagLineItemsTelemetryProps,
) => {
  const { restaurantSlug } = props;

  const { track } = useTelemetry();

  // ─── HELPERS ─────────────────────────────────────────────────────

  const trackNavigateToEditPdpEvent = useCallback(
    (product: PartialProduct) => {
      track('pdp.view', {
        entryPoint: 'bag',
        selectedProduct: telemetryProductFromInput({
          ...product,
          location: restaurantSlug,
        }),
      });
    },
    [restaurantSlug, track],
  );

  const trackRemoveLineItemEvent = useCallback(
    (product: PartialProduct) => {
      track('bag.remove', {
        selectedProduct: telemetryProductFromInput({
          ...product,
          location: restaurantSlug,
        }),
      });
    },
    [restaurantSlug, track],
  );

  const trackModifyLineItemQuantityEvent = useCallback(
    (selectedQuantity: number) => {
      track('bag.adjust_quantity', { quantity: selectedQuantity });
    },
    [track],
  );

  // ─────────────────────────────────────────────────────────────────

  return {
    trackNavigateToEditPdpEvent,
    trackRemoveLineItemEvent,
    trackModifyLineItemQuantityEvent,
  };
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseBagLineItemsTelemetryProps = {
  readonly restaurantSlug: string;
};
